import React, { useState, useEffect, useCallback } from 'react';
import { sharepointAPI } from '../utils/api';

// Breadcrumb-driven folder browser. `rootItemId` (optional) roots the tree at a
// specific folder (used for the DRE-letter picker); omit for the library root.
function SharePointFolderPicker({ title = 'Select a folder', rootItemId = null, rootLabel = 'Library', onSelect, onClose }) {
  const [stack, setStack] = useState([{ id: rootItemId, name: rootLabel }]); // breadcrumb
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const current = stack[stack.length - 1];

  const load = useCallback(async (itemId) => {
    setLoading(true); setError('');
    try {
      const res = await sharepointAPI.browse(itemId || undefined);
      setFolders(res.data.folders || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load folders');
      setFolders([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(current.id); }, [current.id, load]);

  const openFolder = (f) => setStack((s) => [...s, { id: f.id, name: f.name, webUrl: f.webUrl }]);
  const goTo = (idx) => setStack((s) => s.slice(0, idx + 1));
  const pathString = () => stack.map((s) => s.name).join(' / ');

  const chooseCurrent = () => onSelect({ id: current.id, name: current.name, path: pathString(), webUrl: current.webUrl || null });
  const chooseFolder = (f) => onSelect({ id: f.id, name: f.name, path: `${pathString()} / ${f.name}`, webUrl: f.webUrl || null });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-5 max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">{title}</h3>
        {/* Breadcrumb */}
        <div className="text-xs text-gray-500 mb-3 flex flex-wrap gap-1">
          {stack.map((s, i) => (
            <span key={`${s.id || 'root'}-${i}`}>
              {i > 0 && <span className="mx-1 text-gray-300">/</span>}
              <button onClick={() => goTo(i)} className="hover:text-primary-600">{s.name}</button>
            </span>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100 min-h-[200px]">
          {loading ? (
            <div className="text-center py-10 text-sm text-gray-500">Loading&#8230;</div>
          ) : error ? (
            <div className="text-center py-10 text-sm text-red-600">{error}</div>
          ) : folders.length === 0 ? (
            <div className="text-center py-10 text-sm text-gray-400">No subfolders here.</div>
          ) : folders.map((f) => (
            <div key={f.id} className="flex items-center justify-between px-3 py-2 hover:bg-gray-50">
              <button onClick={() => openFolder(f)} className="flex items-center gap-2 text-sm text-gray-800 min-w-0">
                <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path d="M2 6a2 2 0 012-2h4l2 2h6a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" /></svg>
                <span className="truncate">{f.name}</span>
              </button>
              <button onClick={() => chooseFolder(f)} className="text-xs font-medium text-primary-600 hover:text-primary-800 flex-shrink-0 ml-2">Select</button>
            </div>
          ))}
        </div>

        <div className="flex justify-between items-center gap-2 mt-4">
          <button onClick={chooseCurrent} disabled={!current.id} className="text-sm font-medium text-primary-600 hover:text-primary-800 disabled:text-gray-300" title={current.id ? 'Select the current folder' : 'Open a folder to select it'}>
            Select this folder ({current.name})
          </button>
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default SharePointFolderPicker;
